import React, { ReactNode, useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { UnifiedAddressFormCtx } from '@r1-frontend/unified-address-form';

import { InfoCircleFlip } from '@r1-frontend/ui-react/components/svg/main';
import { FullWidthListContainer } from '@r1-frontend/ui-react/experimental/containers';

import { useAnalytics } from '~/src/features/full-buy/analytics/context';
import { Banner } from '~/src/features/full-buy/Banner';
import { TiketContainer } from '~/src/features/full-buy/containers/agregate/TiketContainer';
import { TiketContainerFinal } from '~/src/features/full-buy/containers/agregate/TiketContainerFinal';
import GoToNextStepButton from '~/src/features/full-buy/containers/GoToNextStepButton';
import { GuideFullBuy } from '~/src/features/full-buy/containers/GuideFullBuy';
import ProgressBarWithBackBtnContainer from '~/src/features/full-buy/containers/ProgressBarWithBackBtn';
import { selectCurrentTariff } from '~/src/features/full-buy/store/selectors/selectCurrentTariff';
import { ESteps } from '~/src/features/full-buy/types';
import { TState } from '~/src/store';

import { DoubleColumnLayout } from '~/src/features/full-buy/containers/pages/styled';
import * as ST from './styled';

interface IMainLayout {
    children: ReactNode,
    showFinalTicket?: boolean,
    className?: string,
    isHide?: boolean,
}

export const MainLayout = ({ children, isHide = false, showFinalTicket = false, className }: IMainLayout) => {
    const router = useRouter();
    const analytics = useAnalytics();

    const [isOpenGuide, setIsOpenGuide] = useState(false);

    const {
        selectedTariff,
        stepIndex,
        flow,
    } = useSelector((state: TState) => ({
        selectedTariff: selectCurrentTariff(state),
        stepIndex: state.fullBuy.stepIndex,
        flow: state.fullBuy.flow,
    }), shallowEqual);

    const onCloseGuide = () => {
        setIsOpenGuide(false);
    };

    const onOpenGuide = () => {
        setIsOpenGuide(true);
        analytics.clickGuide(step);
    };

    const step = flow?.steps[stepIndex];

    const isHideGuideIcon = useMemo(() => {
        return (step === ESteps.final) || (!router?.query?.tariffName && step === ESteps.tariffInfo);
    }, [step]);

    return (
        <UnifiedAddressFormCtx salePackageId={selectedTariff?.sale_package_id} analytics={analytics.unifiedAddressForm}>
            <DoubleColumnLayout className={className} isHide={isHide}>
                <FullWidthListContainer $direction={'column'} $size={24}>
                    <ProgressBarWithBackBtnContainer
                        onClickInfoIcon={onOpenGuide}
                        Icon={isHideGuideIcon ? undefined : InfoCircleFlip}
                    />
                    {children}

                    <GoToNextStepButton/>

                    <Banner/>
                </FullWidthListContainer>
                <ST.LeftSide>
                    <ST.RequestContent>
                        {
                            showFinalTicket
                                ? <TiketContainerFinal/>
                                : <TiketContainer/>
                        }
                    </ST.RequestContent>
                </ST.LeftSide>
                <GuideFullBuy isOpen={isOpenGuide} onClose={onCloseGuide}/>
            </DoubleColumnLayout>
        </UnifiedAddressFormCtx>
    );
};
